import { inject, Injectable } from '@angular/core';
import { IReportEmbedConfiguration } from 'powerbi-client';
import { map, Observable, of, shareReplay, switchMap } from 'rxjs';

import {
  ReportingApiResponseBody,
  Workspace,
  Report,
  WorkspaceResponse,
} from '@digital-platform/reporting/domain';
import {
  Dropdown,
  Item,
  NavData,
} from '@digital-platform/ui-base-components/category-list-nav';
import { categoryMap } from './category-map';
import { WorkspaceService } from './workspace/workspace.service';

@Injectable({
  providedIn: 'root',
})
export class ReportingService {
  private readonly workspaceService = inject(WorkspaceService);

  getReportConfig(
    id: string,
    groupId: string
  ): Observable<IReportEmbedConfiguration> {
    return new Observable<IReportEmbedConfiguration>((observer) => {
      const accessToken = 'mock-access-token-1234567890';
      const embedURL = `https://app.powerbi.com/reportEmbed?reportId=${id}&groupId=${groupId}`;
      observer.next({
        id: id,
        embedUrl: embedURL,
        accessToken: accessToken,
      });
      observer.complete();
    });
  }

  /**
   * Returns all reports across all workspaces, mapped to business categories
   * @returns
   */
  getCategorizedReports(
    workspaces?: Workspace[],
    embeddedPageMode = false
  ): Observable<NavData> {
    if (workspaces) {
      return of(
        this.mapReportsToCategories(workspaces, categoryMap, embeddedPageMode)
      );
    }

    // IF NOT PASSED IN: Get the workspaces from the service
    // and map the reports to categories
    return this.workspaceService.getWorkspaces().pipe(
      map(
        (response: ReportingApiResponseBody<WorkspaceResponse>) =>
          response.body.workspaces
      ),
      shareReplay(1),
      switchMap((wrkspcs: Workspace[]) =>
        of(this.mapReportsToCategories(wrkspcs, categoryMap, embeddedPageMode))
      )
    );
  }

  /**
   * Map the static category data map to dynamic data & transform
   * to match the required schema
   * @param workspaces
   * @returns
   */
  mapReportsToCategories(
    workspaces: Workspace[],
    reportCategoryMap: NavData,
    embeddedPageMode = false
  ): NavData {
    const reports = this.getAllReportsFromWorkspaces(workspaces);

    // Check category map items against all report items
    // then populate the cateogry map with the report name
    return [...reportCategoryMap].map((category: Dropdown) => {
      const newCategory = { ...category };
      const items = [...newCategory.items].map((item: Item) => {
        const report = reports.find((r) => r.id === item.id);

        if (report) {
          item.name = report.name;
          item.type = newCategory.name; // Business typing ie. Regulatory & COmpliance, etc.
          item.parentId = report.groupId; // Group/Workspace ID

          // if embedded page mode, set the link to the report
          if (embeddedPageMode) {
            item.link = `/reporting/group/${report.groupId}/report/${report.id}`;
          } else {
            delete item.link;
          }
        }

        return item;
      });

      // Ensure the name exists
      newCategory.items = items.filter((item: Item) => item.name !== '');

      return newCategory;
    }) as NavData;
  }

  /**
   * Helper that extracts report items from the workspace collection
   * @param workspaces
   * @returns
   */
  getAllReportsFromWorkspaces(workspaces: Workspace[]): Report[] {
    return workspaces.reduce((data: Report[], workspace: Workspace) => {
      // Add the workspace/group id to the item for link building later
      workspace.reports?.map((rpt) => {
        rpt['groupId'] = workspace.id;
        return rpt;
      });

      return data.concat([...(workspace.reports || [])]);
    }, [] as Report[]);
  }
}
